import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import { ReactComponent as AskAiIcon } from "../../assets/icons/ask_ai.svg";
import { ReactComponent as BookmarkIcon } from "../../assets/icons/bookmarkList.svg";
import { ReactComponent as DisplaySizeIcon } from "../../assets/icons/display_size.svg";
import { ReactComponent as FitPageIcon } from "../../assets/icons/fit_page.svg";
import { ReactComponent as FitWidthIcon } from "../../assets/icons/fit_width.svg";
import { ReactComponent as FullScreenIcon } from "../../assets/icons/fullScreen.svg";
import { ReactComponent as InfoIcon } from "../../assets/icons/info.svg";
import { ReactComponent as NotebookIcon } from "../../assets/icons/notebook.svg";
import { ReactComponent as ReadGroupIcon } from "../../assets/icons/read_group.svg";
import useReaderStore from "../../context/readerStore";
import {
  useIsMobile,
  useIsTablet,
  useIsTouchTablet,
} from "../../hooks/useMediaQuery";
import { isFeatureAllowed } from "../../services/drmPolicyService";
import {
  showComingSoonNotification,
  showErrorNotification,
} from "../../services/notificationService";
import { formatPageForDisplay } from "../../services/pdfService";
import "./RightToolbar.css";

const RightToolbar = () => {
  const {
    currentPage,
    currentPageEnd,
    totalPages,
    setScale,
    setCurrentPage,
    setCurrentPageEnd,
    toggleBookmarksSidebar,
    isBookmarksSidebarOpen,
    toggleNotesSidebar,
    isNotesSidebarOpen,
    toggleReadingAssistantSidebar,
    isReadingAssistantSidebarOpen,
    togglePublicationDetailsSidebar,
    isPublicationDetailsSidebarOpen,
    documentType,
    readerViewMode,
    setReaderViewMode,
    pageViewMode,
    metadata,
    drmPolicy,
  } = useReaderStore();

  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const isTouchTablet = useIsTouchTablet();
  // Check if EPUB is in double page mode (desktop only)
  const isEpubDoublePageMode =
    documentType === "epub" &&
    !isMobile &&
    !isTouchTablet &&
    pageViewMode === "double";

  const isPdfDoublePageMode =
    documentType === "pdf" &&
    !isMobile &&
    !isTablet &&
    !isTouchTablet &&
    pageViewMode === "double";

  const effectiveCurrentPageEnd =
    documentType === "pdf" && isPdfDoublePageMode && currentPage < totalPages
      ? currentPage + 1
      : currentPageEnd;

  const [isPageInputActive, setIsPageInputActive] = useState(false);
  const [pageInputValue, setPageInputValue] = useState("");
  const [isViewModeDropdownOpen, setIsViewModeDropdownOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(
    !!document.fullscreenElement,
  );
  const [keyboardOffset, setKeyboardOffset] = useState(0);
  const pageInputRef = useRef(null);
  const viewModeDropdownRef = useRef(null);
  const viewModeDropdownPortalRef = useRef(null);
  const sidebarRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const { t } = useTranslation();
  const aiAllowed = isFeatureAllowed(drmPolicy, "ai_features");
  const metadataFirstPage = Number(metadata?.first_page);
  const showRomanDisplayBox =
    documentType === "pdf" &&
    Number.isFinite(metadataFirstPage) &&
    metadataFirstPage > 1;

  const handleToggleBookmarksSidebar = useCallback(() => {
    const wasOpen = isBookmarksSidebarOpen;
    toggleBookmarksSidebar();
    if (!wasOpen) {
      setTimeout(() => {
        document.querySelector(".bookmarks h3")?.focus({ preventScroll: true });
      }, 100);
    }
  }, [isBookmarksSidebarOpen, toggleBookmarksSidebar]);

  const handleToggleNotesSidebar = useCallback(() => {
    const wasOpen = isNotesSidebarOpen;
    toggleNotesSidebar();
    if (!wasOpen) {
      setTimeout(() => {
        document.querySelector(".notes h3")?.focus({ preventScroll: true });
      }, 100);
    }
  }, [isNotesSidebarOpen, toggleNotesSidebar]);

  const handleToggleReadingAssistantSidebar = useCallback(() => {
    const wasOpen = isReadingAssistantSidebarOpen;
    toggleReadingAssistantSidebar();
    if (!wasOpen) {
      setTimeout(() => {
        document
          .querySelector(".reading-assistant h3")
          ?.focus({ preventScroll: true });
      }, 100);
    }
  }, [isReadingAssistantSidebarOpen, toggleReadingAssistantSidebar]);

  const handleTogglePublicationDetailsSidebar = useCallback(() => {
    const wasOpen = isPublicationDetailsSidebarOpen;
    togglePublicationDetailsSidebar();
    if (!wasOpen) {
      setTimeout(() => {
        document
          .querySelector(".publication-details h3")
          ?.focus({ preventScroll: true });
      }, 100);
    }
  }, [isPublicationDetailsSidebarOpen, togglePublicationDetailsSidebar]);

  const handleToggleFullscreen = useCallback(() => {
    try {
      const isFull = !!document.fullscreenElement;
      if (!isFull) {
        const root = document.documentElement;
        if (root.requestFullscreen) root.requestFullscreen();
      } else {
        if (document.exitFullscreen) document.exitFullscreen();
      }
    } catch (e) {
      // Ignore fullscreen errors
    }
    setIsViewModeDropdownOpen(false);
  }, []);

  // Sync fullscreen state when user toggles via button or e.g. Escape key
  useEffect(() => {
    const onFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener("fullscreenchange", onFullscreenChange);
    return () =>
      document.removeEventListener("fullscreenchange", onFullscreenChange);
  }, []);

  // Handle iPad keyboard overlap using Visual Viewport API
  useEffect(() => {
    if (!window.visualViewport) return;

    const handleViewportChange = () => {
      const viewport = window.visualViewport;
      const layoutViewportHeight = window.innerHeight;
      const visualViewportHeight = viewport.height;

      // Calculate keyboard height: gap between layout and visual viewport
      const keyboardHeight = Math.max(
        0,
        layoutViewportHeight - visualViewportHeight,
      );

      setKeyboardOffset(keyboardHeight);
    };

    // Listen to visual viewport changes (keyboard show/hide, zoom, etc.)
    window.visualViewport.addEventListener("resize", handleViewportChange);
    window.visualViewport.addEventListener("scroll", handleViewportChange);

    return () => {
      window.visualViewport.removeEventListener("resize", handleViewportChange);
      window.visualViewport.removeEventListener("scroll", handleViewportChange);
    };
  }, []);

  const handleToggleViewModeDropdown = useCallback(() => {
    setIsViewModeDropdownOpen((prev) => !prev);
  }, []);

  const handleFitToWidth = useCallback(() => {
    // Dispatch custom event that viewers can listen to
    window.dispatchEvent(new CustomEvent("reader:fit-to-width"));
    setReaderViewMode("fit-to-width");
    // align zoom state baseline so UI reflects adjusted mode fast
    setScale(1.0);
    setIsViewModeDropdownOpen(false);
  }, [setReaderViewMode, setScale]);

  const handleFitToPage = useCallback(() => {
    // Dispatch custom event that viewers can listen to
    window.dispatchEvent(new CustomEvent("reader:fit-to-page"));
    setReaderViewMode("fit-to-page");
    setScale(1.0);
    setIsViewModeDropdownOpen(false);
  }, [setReaderViewMode, setScale]);

  // NOTE: view selection is handled via dedicated handlers (handleFitToWidth/handleFitToPage)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      const clickedInsideAnchor =
        viewModeDropdownRef.current &&
        viewModeDropdownRef.current.contains(e.target);
      const clickedInsidePortal =
        viewModeDropdownPortalRef.current &&
        viewModeDropdownPortalRef.current.contains(e.target);
      if (!clickedInsideAnchor && !clickedInsidePortal) {
        setIsViewModeDropdownOpen(false);
      }
    };

    if (isViewModeDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [isViewModeDropdownOpen]);

  // Close dropdown on Escape
  useEffect(() => {
    const handleKeydown = (e) => {
      if (e.key === "Escape") setIsViewModeDropdownOpen(false);
    };

    if (isViewModeDropdownOpen) {
      document.addEventListener("keydown", handleKeydown);
      return () => document.removeEventListener("keydown", handleKeydown);
    }
  }, [isViewModeDropdownOpen]);

  const handleNextPage = useCallback(() => {
    if (currentPage < totalPages) {
      const step = isPdfDoublePageMode ? 2 : 1;
      const nextPage = Math.min(totalPages, currentPage + step);
      setCurrentPage(nextPage);
      window.dispatchEvent(
        new CustomEvent("reader:go-to-page", { detail: { page: nextPage } }),
      );
    }
  }, [currentPage, totalPages, setCurrentPage, isPdfDoublePageMode]);

  const handlePrevPage = useCallback(() => {
    if (currentPage > 1) {
      const step = isPdfDoublePageMode ? 2 : 1;
      const prevPage = Math.max(1, currentPage - step);
      setCurrentPage(prevPage);
      window.dispatchEvent(
        new CustomEvent("reader:go-to-page", { detail: { page: prevPage } }),
      );
    }
  }, [currentPage, setCurrentPage, isPdfDoublePageMode]);

  // Focus page input when it becomes active
  useEffect(() => {
    if (isPageInputActive && pageInputRef.current) {
      pageInputRef.current.focus();
      pageInputRef.current.select();
    }
  }, [isPageInputActive]);

  // Position portal-based dropdown on open
  useEffect(() => {
    if (!isViewModeDropdownOpen || !viewModeDropdownRef.current) return;

    // Default: position above the anchor (like bottom: 100%)
    const computePosition = () => {
      const margin = 8;
      const dropdownEl = viewModeDropdownPortalRef.current;
      const viewportW = window.innerWidth;
      const anchorRect = viewModeDropdownRef.current.getBoundingClientRect();

      // Default left: align right edge of dropdown to right edge of anchor
      const dropdownWidth = dropdownEl ? dropdownEl.offsetWidth : 180;
      const dropdownHeight = dropdownEl ? dropdownEl.offsetHeight : 80;

      let left = anchorRect.right - dropdownWidth;
      let top = anchorRect.top - dropdownHeight - margin;

      // If above doesn't fit, position below anchor
      if (top < 0) {
        top = anchorRect.bottom + margin;
      }

      // Prevent horizontal overflow
      if (left < 8) left = 8;
      if (left + dropdownWidth > viewportW - 8)
        left = viewportW - dropdownWidth - 8;

      setDropdownPosition({ top, left });
    };

    // Compute once and recompute on resize/scroll
    computePosition();
    // Recompute on next frame and shortly after to ensure we capture dimensions
    requestAnimationFrame(computePosition);
    const timer = setTimeout(computePosition, 60);
    window.addEventListener("resize", computePosition);
    window.addEventListener("scroll", computePosition, true);
    return () => {
      window.removeEventListener("resize", computePosition);
      window.removeEventListener("scroll", computePosition, true);
      clearTimeout(timer);
    };
  }, [isViewModeDropdownOpen]);

  // Page input handlers
  const handlePageInputClick = useCallback(() => {
    setIsPageInputActive(true);
    setPageInputValue(currentPage.toString());
  }, [currentPage]);

  const handlePageInputChange = useCallback((e) => {
    const value = e.target.value;
    setPageInputValue(value);
  }, []);

  const handlePageInputSubmit = useCallback(() => {
    const pageNumber = parseInt(pageInputValue, 10);

    if (isNaN(pageNumber)) {
      showErrorNotification(t("rightToolbar.pageInputInvalid"));
      return;
    }

    if (pageNumber < 1 || pageNumber > totalPages) {
      showErrorNotification(
        t("rightToolbar.pageInputOutOfRange", { total: totalPages }),
      );
      return;
    }

    setCurrentPageEnd(null);
    setCurrentPage(pageNumber);
    window.dispatchEvent(
      new CustomEvent("reader:go-to-page", { detail: { page: pageNumber } }),
    );

    setIsPageInputActive(false);
    setPageInputValue("");
  }, [pageInputValue, totalPages, setCurrentPage, setCurrentPageEnd, t]);

  const handlePageInputCancel = useCallback(() => {
    setTimeout(() => {
      setIsPageInputActive(false);
      setPageInputValue("");
    }, 100);
  }, []);

  const handlePageInputKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handlePageInputSubmit();
      } else if (e.key === "Escape") {
        e.preventDefault();
        handlePageInputCancel();
      }
    },
    [handlePageInputSubmit, handlePageInputCancel],
  );

  return (
    <div
      className="right-sidebar"
      ref={sidebarRef}
      style={{
        paddingBottom: `max(48px, calc(env(safe-area-inset-bottom, 48px) + ${keyboardOffset}px))`,
      }}
    >
      <div className="sidebar-top">
        {/* Publication Details */}
        <button
          className={`sidebar-icon-button${
            isPublicationDetailsSidebarOpen ? " is-active" : ""
          }`}
          type="button"
          onClick={handleTogglePublicationDetailsSidebar}
          aria-pressed={isPublicationDetailsSidebarOpen}
          aria-expanded={isPublicationDetailsSidebarOpen}
          aria-label={
            isPublicationDetailsSidebarOpen
              ? t("rightToolbar.hidePublicationDetails")
              : t("rightToolbar.showPublicationDetails")
          }
          title={
            isPublicationDetailsSidebarOpen
              ? t("rightToolbar.hidePublicationDetails")
              : t("rightToolbar.showPublicationDetails")
          }
        >
          <InfoIcon className="icon-v2 icon-v2-info" aria-hidden="true" />
        </button>

        {aiAllowed && (
          <button
            className={`sidebar-icon-button${
              isReadingAssistantSidebarOpen ? " is-active" : ""
            }`}
            type="button"
            onClick={handleToggleReadingAssistantSidebar}
            aria-pressed={isReadingAssistantSidebarOpen}
            aria-expanded={isReadingAssistantSidebarOpen}
            aria-label={
              isReadingAssistantSidebarOpen
                ? t("rightToolbar.hideReadingAssistant")
                : t("rightToolbar.showReadingAssistant")
            }
            title={
              isReadingAssistantSidebarOpen
                ? t("rightToolbar.hideReadingAssistant")
                : t("rightToolbar.showReadingAssistant")
            }
          >
            <AskAiIcon
              className="preserve-original-fill icon-v2"
              aria-hidden="true"
            />
          </button>
        )}

        {isFeatureAllowed(drmPolicy, "bookmarks") && (
          <button
            className={`sidebar-icon-button${
              isBookmarksSidebarOpen ? " is-active" : ""
            }`}
            onClick={handleToggleBookmarksSidebar}
            aria-pressed={isBookmarksSidebarOpen}
            aria-expanded={isBookmarksSidebarOpen}
            aria-label={
              isBookmarksSidebarOpen
                ? t("rightToolbar.hideBookmarks")
                : t("rightToolbar.showBookmarks")
            }
            title={
              isBookmarksSidebarOpen
                ? t("rightToolbar.hideBookmarks")
                : t("rightToolbar.showBookmarks")
            }
          >
            <BookmarkIcon className="icon-bookmark" aria-hidden="true" />
          </button>
        )}

        {isFeatureAllowed(drmPolicy, "notes") && (
          <button
            className={`sidebar-icon-button${
              isNotesSidebarOpen ? " is-active" : ""
            }`}
            onClick={handleToggleNotesSidebar}
            aria-pressed={isNotesSidebarOpen}
            aria-expanded={isNotesSidebarOpen}
            aria-label={t("rightToolbar.myNotebook")}
            title={t("rightToolbar.myNotebook")}
          >
            <NotebookIcon className="icon-v2" aria-hidden="true" />
          </button>
        )}

        {/* Reading Group — placeholder, no action yet */}
        <button
          className="sidebar-icon-button is-coming-soon"
          type="button"
          aria-label={t("rightToolbar.readingGroup")}
          title={t("rightToolbar.readingGroup")}
          onClick={() => showComingSoonNotification("reading_group")}
        >
          <ReadGroupIcon className="icon-v2 icon-v2-thin" aria-hidden="true" />
        </button>
      </div>

      {documentType && (
        <div className="sidebar-bottom">
          <div className="page-controls">
            <div className="page-navigation-vertical">
              {isPageInputActive ? (
                <form
                  className="page-input-container-vertical"
                  onSubmit={(e) => {
                    e.preventDefault();
                    handlePageInputSubmit();
                  }}
                >
                  <input
                    ref={pageInputRef}
                    type="number"
                    min="1"
                    max={totalPages}
                    value={pageInputValue}
                    onChange={handlePageInputChange}
                    onKeyDown={handlePageInputKeyDown}
                    onBlur={handlePageInputCancel}
                    className="page-input-vertical"
                    placeholder={t("rightToolbar.pageInputPlaceholder", {
                      min: 1,
                      max: totalPages,
                    })}
                    title={t("rightToolbar.pageInputTitle", {
                      min: 1,
                      max: totalPages,
                    })}
                    enterKeyHint="go"
                    inputMode="numeric"
                  />
                </form>
              ) : (
                <div className="page-info-vertical">
                  {showRomanDisplayBox && (
                    <div
                      className="roman-page-box"
                      title={t("rightToolbar.currentPageLabel", {
                        current: formatPageForDisplay(
                          currentPage,
                          metadata,
                          effectiveCurrentPageEnd,
                        ),
                      })}
                      aria-label={t("rightToolbar.currentPageLabel", {
                        current: formatPageForDisplay(
                          currentPage,
                          metadata,
                          effectiveCurrentPageEnd,
                        ),
                      })}
                    >
                      {formatPageForDisplay(
                        currentPage,
                        metadata,
                        effectiveCurrentPageEnd,
                      )}
                    </div>
                  )}
                  <div
                    className="current-page-box"
                    onClick={handlePageInputClick}
                    title={t("rightToolbar.enterPageNumber")}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handlePageInputClick();
                      }
                    }}
                    aria-label={t("rightToolbar.currentPageLabel", {
                      current:
                        effectiveCurrentPageEnd !== null &&
                        currentPage !== effectiveCurrentPageEnd
                          ? `${currentPage}-${effectiveCurrentPageEnd}`
                          : currentPage,
                    })}
                  >
                    {effectiveCurrentPageEnd !== null &&
                    currentPage !== effectiveCurrentPageEnd
                      ? `${currentPage}-${effectiveCurrentPageEnd}`
                      : currentPage}
                  </div>
                  <div className="total-pages-text">{totalPages}</div>
                </div>
              )}
            </div>

            {documentType !== "epub" && (
              <button
                className="nav-icon-button"
                onClick={handlePrevPage}
                disabled={currentPage <= 1}
                aria-label={t("rightToolbar.previousPage")}
                title={t("rightToolbar.previousPage")}
              >
                <FaChevronUp size={14} />
              </button>
            )}

            {/* Fit to page / fit to width — between navigation arrows */}
            <div
              className="sidebar-view-mode-container"
              ref={viewModeDropdownRef}
            >
              <button
                className="sidebar-icon-button view-mode-button"
                onClick={handleToggleViewModeDropdown}
                aria-label={t("rightToolbar.viewModeOptions")}
                title={t("rightToolbar.viewModeOptions")}
                aria-expanded={isViewModeDropdownOpen}
              >
                <DisplaySizeIcon className="icon-md-lg" aria-hidden="true" />
              </button>

              {isViewModeDropdownOpen &&
                createPortal(
                  <div
                    ref={viewModeDropdownPortalRef}
                    className="view-mode-dropdown view-mode-dropdown--portal"
                    style={{
                      top: dropdownPosition.top,
                      left: dropdownPosition.left,
                    }}
                    role="menu"
                  >
                    {(documentType === "pdf" ||
                      (documentType === "epub" && !isTouchTablet)) && (
                      <>
                        <button
                          className={`view-mode-option${
                            readerViewMode === "fit-to-width" ? " active" : ""
                          }${isEpubDoublePageMode ? " disabled" : ""}`}
                          onClick={
                            isEpubDoublePageMode ? undefined : handleFitToWidth
                          }
                          disabled={isEpubDoublePageMode}
                          title={
                            isEpubDoublePageMode
                              ? t("rightToolbar.disabledInDoublePage")
                              : ""
                          }
                        >
                          <FitWidthIcon
                            className="view-mode-option-icon"
                            aria-hidden="true"
                          />
                          <span className="view-mode-option-label">
                            {t("rightToolbar.fitToWidth")}
                          </span>
                          {readerViewMode === "fit-to-width" &&
                            !isEpubDoublePageMode && (
                              <span className="check">✓</span>
                            )}
                        </button>
                        <button
                          className={`view-mode-option${
                            readerViewMode === "fit-to-page" ? " active" : ""
                          }${isEpubDoublePageMode ? " disabled" : ""}`}
                          onClick={
                            isEpubDoublePageMode ? undefined : handleFitToPage
                          }
                          disabled={isEpubDoublePageMode}
                          title={
                            isEpubDoublePageMode
                              ? t("rightToolbar.disabledInDoublePage")
                              : ""
                          }
                        >
                          <FitPageIcon
                            className="view-mode-option-icon"
                            aria-hidden="true"
                          />
                          <span className="view-mode-option-label">
                            {t("rightToolbar.fitToPage")}
                          </span>
                          {readerViewMode === "fit-to-page" &&
                            !isEpubDoublePageMode && (
                              <span className="check">✓</span>
                            )}
                        </button>
                      </>
                    )}
                    <button
                      className="view-mode-option"
                      onClick={handleToggleFullscreen}
                    >
                      <FullScreenIcon
                        className="view-mode-option-icon view-mode-option-icon--fullscreen"
                        aria-hidden="true"
                      />
                      <span className="view-mode-option-label">
                        {isFullscreen
                          ? t("rightToolbar.exitFullscreen")
                          : t("rightToolbar.enterFullscreen")}
                      </span>
                    </button>
                  </div>,
                  document.body,
                )}
            </div>

            {documentType !== "epub" && (
              <button
                className="nav-icon-button"
                onClick={handleNextPage}
                disabled={currentPage >= totalPages}
                aria-label={t("rightToolbar.nextPage")}
                title={t("rightToolbar.nextPage")}
              >
                <FaChevronDown size={14} />
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default RightToolbar;
